import type {
  CollisionDetection,
  DragEndEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  DndContext as DndKitContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToVerticalAxis,
  restrictToWindowEdges,
} from "@dnd-kit/modifiers";
import type { ReactNode } from "react";
import { useState } from "react";

import type { SpaceGroupWithSpaces } from ".";
import {
  getDragData,
  isSpaceDragData,
  isSpaceGroupDragData,
} from "../SidebarV2/CommunityMenu/SpaceGroupList/typeGuards";
import type { ActiveItem } from "../SidebarV2/CommunityMenu/SpaceGroupList/types";
import { SpaceListDragOverlay } from "../SidebarV2/SpaceListDragOverlay";

const customCollisionDetection: CollisionDetection = args => {
  const { active, droppableContainers, ...rest } = args;
  const activeType = (active.data.current as { type?: string } | undefined)
    ?.type;

  const filtered = droppableContainers.filter(container => {
    if (activeType === "spaceGroup") {
      return (
        (container.data.current as { type?: string } | undefined)?.type ===
        "spaceGroup"
      );
    }
    return true;
  });

  return closestCenter({ ...rest, active, droppableContainers: filtered });
};

interface DndContextProps {
  children: ReactNode;
  onSpaceGroupSortEnd: (event: { activeId: number; overId: number }) => void;
  onSpaceSortEnd: (event: {
    activeId: number;
    overId: number;
    spaceGroupId: number;
  }) => void;
  spaceGroupWithSpaces: SpaceGroupWithSpaces[];
}

export function DndContext({
  children,
  onSpaceGroupSortEnd,
  onSpaceSortEnd,
  spaceGroupWithSpaces,
}: DndContextProps) {
  const [activeItem, setActiveItem] = useState<ActiveItem | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 500,
        tolerance: 10,
      },
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!active || !over) return;
    if (active.id === over.id) return;

    const activeDragData = getDragData(active);
    const overDragData = getDragData(over);

    if (!activeDragData || !overDragData) return;

    if (
      isSpaceGroupDragData(activeDragData) &&
      isSpaceGroupDragData(overDragData)
    ) {
      onSpaceGroupSortEnd({
        activeId: activeDragData.id,
        overId: overDragData.id,
      });
    } else if (
      isSpaceDragData(activeDragData) &&
      isSpaceDragData(overDragData)
    ) {
      onSpaceSortEnd({
        activeId: activeDragData.id,
        overId: overDragData.id,
        spaceGroupId: overDragData.spaceGroupId,
      });
    }

    setActiveItem(null);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const dragData = getDragData(active);

    if (!dragData) return;

    if (isSpaceGroupDragData(dragData)) {
      const spaceGroup = spaceGroupWithSpaces.find(
        group => group.id === dragData.id,
      );
      if (!spaceGroup) return;
      setActiveItem({ item: spaceGroup, type: "spaceGroup" });
    } else if (isSpaceDragData(dragData)) {
      const space = spaceGroupWithSpaces
        .flatMap(spaceGroup => spaceGroup.spaces)
        .find(space => space.id === dragData.id);
      if (!space) return;
      setActiveItem({ item: space, type: "space" });
    }
  };

  const handleDragCancel = () => {
    setActiveItem(null);
  };

  return (
    <DndKitContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      {children}
      <DragOverlay>
        {activeItem && <SpaceListDragOverlay activeItem={activeItem} />}
      </DragOverlay>
    </DndKitContext>
  );
}
